'use client';

import { c as _c } from "react/compiler-runtime";
import { Divider } from '@common/components';
import { cn } from '@core/utils';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { ENV } from '@/constants';
import { openResponsiveDialog } from '@/stores/ResponsiveDialogStore';
import { MarketPhotoReviewGrid } from '../../../_components/MarketPhotoReviewGrid';
import { MarketReviewCard } from '../../../_components/MarketReviewCard';
import { MarketReviewDetailContent, type MarketReviewItem } from '../../../_components/MarketReviewDetailContent';
import { MarketReviewSummary } from '../../../_components/MarketReviewSummary';
import { PhotoReviewModalContent } from '../../../_components/PhotoReviewModalContent';
import { TABS } from '../constants';

// ── Types ──

type SortType = 'LATEST' | 'RATING_HIGH';
interface FileDto {
  url?: string;
  type?: string;
}
interface ReviewDto {
  reviewId?: string;
  rating?: number;
  contents?: string;
  nickname?: string;
  productOptionName?: string;
  fileUrls?: FileDto[];
  createdAt?: string;
  answer?: string;
  answerAt?: string;
}
interface SummaryDto {
  averageRating?: number;
  totalCount?: number;
  photoReviewCount?: number;
}
interface PageInfoDto {
  currentPage?: number;
  totalPages?: number;
  totalCount?: number;
  hasNext?: boolean;
}
interface ReviewListResponse {
  summary?: SummaryDto;
  reviews?: ReviewDto[];
  pageInfo?: PageInfoDto;
}
interface PhotoDto {
  reviewId?: string;
  imageUrl?: string;
}
interface PhotoReviewListResponse {
  photoReviewCount?: number;
  photos?: PhotoDto[];
  pageInfo?: {
    page?: number;
    size?: number;
    totalCount?: number;
    hasNext?: boolean;
  };
}

// ── Fetch ──

const PAGE_SIZE = 10;
const fetchReviews = async (productId: string, page: number, size: number, sort: SortType): Promise<ReviewListResponse> => {
  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
    sort
  });
  const res = await fetch(`${ENV.NEXT_PUBLIC_API_DOMAIN}/wello/v2/allows/local-market/products/${productId}/reviews?${params}`);
  const data = await res.json();
  return data.context as ReviewListResponse;
};
const fetchPhotoReviews = async (productId: string, page: number, size: number): Promise<PhotoReviewListResponse> => {
  const res = await fetch(`${ENV.NEXT_PUBLIC_API_DOMAIN}/wello/v2/allows/local-market/products/${productId}/reviews/photos?page=${page}&size=${size}`);
  const data = await res.json();
  return data.context as PhotoReviewListResponse;
};
interface ReviewDetailResponse {
  reviewId?: string;
  rating?: number;
  contents?: string;
  nickname?: string;
  productOptionName?: string;
  fileUrls?: FileDto[];
  createdAt?: string;
}
const fetchReviewDetail = async (productId: string, reviewId: string): Promise<ReviewDetailResponse> => {
  const res = await fetch(`${ENV.NEXT_PUBLIC_API_DOMAIN}/wello/v2/allows/local-market/products/${productId}/reviews/${reviewId}`);
  const data = await res.json();
  return data.context as ReviewDetailResponse;
};

// ── Helpers ──

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const yy = String(d.getFullYear()).slice(2);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yy}.${mm}.${dd}`;
};

// ── Review Detail Wrapper ──

const ReviewDetailWrapper = t0 => {
  const $ = _c(9);
  const {
    productId,
    reviewId,
    imageUrl
  } = t0;
  let t1;
  if ($[0] !== productId || $[1] !== reviewId) {
    t1 = {
      queryKey: ["local-market-review-detail", productId, reviewId],
      queryFn: () => fetchReviewDetail(productId, reviewId)
    };
    $[0] = productId;
    $[1] = reviewId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data,
    isLoading
  } = useQuery(t1);
  let t2;
  if ($[3] !== data || $[4] !== imageUrl || $[5] !== isLoading) {
    t2 = data ? {
      rating: data.rating ?? 0,
      nickname: data.nickname ?? null,
      date: formatDate(data.createdAt),
      content: data.contents ?? "",
      imageUrlList: data.fileUrls?.flatMap(_temp) ?? [],
      productName: data.productOptionName
    } : {
      rating: 0,
      nickname: null,
      date: "",
      content: isLoading ? "\uBD88\uB7EC\uC624\uB294 \uC911..." : "",
      imageUrlList: imageUrl ? [imageUrl] : []
    };
    $[3] = data;
    $[4] = imageUrl;
    $[5] = isLoading;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const item = t2;
  let t3;
  if ($[7] !== item) {
    t3 = <MarketReviewDetailContent reviews={[item]} initialIndex={0} />;
    $[7] = item;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};

// ── Component ──

interface TabReviewProps {
  productId: string;
}
export const TabReview = t0 => {
  const $ = _c(37);
  const {
    productId
  } = t0;
  const [sortType, setSortType] = useState("LATEST");
  let t1;
  if ($[0] !== productId || $[1] !== sortType) {
    t1 = {
      queryKey: ["local-market-reviews", productId, sortType],
      queryFn: t2 => {
        const {
          pageParam: t3
        } = t2;
        const pageParam = t3 === undefined ? 1 : t3;
        return fetchReviews(productId, pageParam as number, PAGE_SIZE, sortType);
      },
      getNextPageParam: _temp2,
      initialPageParam: 1
    };
    $[0] = productId;
    $[1] = sortType;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading
  } = useInfiniteQuery(t1);
  const summary = data?.pages[0]?.summary;
  let t2;
  if ($[3] !== data?.pages) {
    t2 = data?.pages.flatMap(_temp3) ?? [];
    $[3] = data?.pages;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const allReviews = t2;
  let t3;
  if ($[5] !== allReviews) {
    t3 = allReviews.map(_temp5);
    $[5] = allReviews;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const reviewItems = t3;
  let t4;
  if ($[7] !== productId) {
    t4 = {
      queryKey: ["local-market-photo-reviews", productId],
      queryFn: () => fetchPhotoReviews(productId, 1, 10)
    };
    $[7] = productId;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    data: photoData
  } = useQuery(t4);
  let t5;
  if ($[9] !== photoData?.photos) {
    t5 = (photoData?.photos ?? []).map(_temp6).filter(_temp7);
    $[9] = photoData?.photos;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const photoReviews = t5;
  let t6;
  if ($[11] !== isLoading || $[12] !== summary?.averageRating || $[13] !== summary?.totalCount) {
    t6 = isLoading ? null : <MarketReviewSummary rating={summary?.averageRating ?? 0} reviewCount={summary?.totalCount ?? 0} />;
    $[11] = isLoading;
    $[12] = summary?.averageRating;
    $[13] = summary?.totalCount;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== photoReviews || $[16] !== productId || $[17] !== reviewItems || $[18] !== summary?.photoReviewCount) {
    t7 = photoReviews.length > 0 && <MarketPhotoReviewGrid photoReviews={photoReviews} totalCount={summary?.photoReviewCount ?? 0} onMoreClick={() => {
      openResponsiveDialog({
        component: PhotoReviewModalContent,
        modalHeader: "\uD3EC\uD1A0\uD6C4\uAE30 \uC804\uCCB4\uBCF4\uAE30",
        className: {
          modal: "max-w-[900px]"
        },
        props: {
          photoReviews,
          reviewItems
        },
        drawerNoPadding: true
      });
    }} onPhotoClick={reviewId => {
      const photo = photoReviews.find(photoReview_0 => photoReview_0.reviewId === reviewId);
      openResponsiveDialog({
        component: ReviewDetailWrapper,
        modalHeader: "\uD6C4\uAE30 \uC0C1\uC138",
        className: {
          modal: "max-w-[900px]"
        },
        props: {
          productId,
          reviewId,
          imageUrl: photo?.imageUrl
        },
        drawerNoPadding: true
      });
    }} />;
    $[15] = photoReviews;
    $[16] = productId;
    $[17] = reviewItems;
    $[18] = summary?.photoReviewCount;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== allReviews.length || $[21] !== sortType) {
    t8 = allReviews.length > 0 && <div className="flex items-center justify-end border-b border-Gray-200 pb-12 pt-32"><button type="button" className={cn("font-700 typo-body4", sortType === "LATEST" ? "text-Gray-600" : "text-Gray-400")} onClick={() => setSortType("LATEST")}>최신순</button><Divider variant="vertical" className="mx-8 !h-12" /><button type="button" className={cn("font-700 typo-body4", sortType === "RATING_HIGH" ? "text-Gray-600" : "text-Gray-400")} onClick={() => setSortType("RATING_HIGH")}>평점 높은순</button></div>;
    $[20] = allReviews.length;
    $[21] = sortType;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== allReviews || $[24] !== isLoading || $[25] !== reviewItems) {
    t9 = isLoading ? null : allReviews.length === 0 ? <div className="flex flex-col items-center justify-center gap-8 py-80"><p className="font-500 text-Gray-600 typo-body3">아직 등록된 후기가 없어요.</p><p className="font-400 text-Gray-500 typo-body4">첫 번째 후기를 작성해보세요.</p></div> : <div className="flex flex-col">{allReviews.map((review_1, index) => <div key={review_1.reviewId}><MarketReviewCard rating={review_1.rating ?? 0} reviewer={review_1.nickname} date={formatDate(review_1.createdAt)} content={review_1.contents ?? ""} imageUrlList={review_1.fileUrls?.flatMap(_temp8) ?? []} productName={review_1.productOptionName} answer={review_1.answer} answerDate={formatDate(review_1.answerAt)} onClick={() => {
          openResponsiveDialog({
            component: MarketReviewDetailContent,
            modalHeader: "\uD6C4\uAE30 \uC0C1\uC138",
            className: {
              modal: "max-w-[900px]"
            },
            props: {
              reviews: reviewItems,
              initialIndex: index
            },
            drawerNoPadding: true
          });
        }} />{index < allReviews.length - 1 && <Divider />}</div>)}</div>;
    $[23] = allReviews;
    $[24] = isLoading;
    $[25] = reviewItems;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== fetchNextPage || $[28] !== hasNextPage || $[29] !== isFetchingNextPage) {
    t10 = hasNextPage && <button type="button" className="mt-8 w-full border border-Gray-300 py-12 font-600 text-Gray-700 typo-body3 disabled:opacity-50" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>{isFetchingNextPage ? "\uBD88\uB7EC\uC624\uB294 \uC911..." : "\uB354\uBCF4\uAE30"}</button>;
    $[27] = fetchNextPage;
    $[28] = hasNextPage;
    $[29] = isFetchingNextPage;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== t10 || $[32] !== t6 || $[33] !== t7 || $[34] !== t8 || $[35] !== t9) {
    t11 = <section id={TABS.REVIEW}><div className="flex flex-col gap-16 px-20">{t6}{t7}{t8}{t9}{t10}</div></section>;
    $[31] = t10;
    $[32] = t6;
    $[33] = t7;
    $[34] = t8;
    $[35] = t9;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  return t11;
};
function _temp(file) {
  return file.type === "IMAGE" && file.url ? [file.url] : [];
}
function _temp2(lastPage) {
  return lastPage.pageInfo?.hasNext ? (lastPage.pageInfo.currentPage ?? 1) + 1 : undefined;
}
function _temp3(page) {
  return page.reviews ?? [];
}
function _temp4(file) {
  return file.type === "IMAGE" && file.url ? [file.url] : [];
}
function _temp5(review) {
  return {
    rating: review.rating ?? 0,
    nickname: review.nickname ?? null,
    date: formatDate(review.createdAt),
    content: review.contents ?? "",
    imageUrlList: review.fileUrls?.flatMap(_temp4) ?? [],
    productName: review.productOptionName,
    answer: review.answer,
    answerDate: formatDate(review.answerAt)
  };
}
function _temp6(photoReview) {
  return {
    imageUrl: photoReview.imageUrl ?? "",
    reviewId: photoReview.reviewId ?? ""
  };
}
function _temp7(review_0) {
  return review_0.imageUrl;
}
function _temp8(file_0) {
  return file_0.type === "IMAGE" && file_0.url ? [file_0.url] : [];
}